import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const OrderingWidget = () => {
  const [selectedCategory, setSelectedCategory] = useState('popular');
  const [quantities, setQuantities] = useState({});

  const categories = [
    { id: 'popular', name: 'Most Popular', icon: 'TrendingUp' },
    { id: 'quick', name: 'Quick Bites', icon: 'Clock' },
    { id: 'healthy', name: 'Healthy Options', icon: 'Leaf' },
    { id: 'family', name: 'Family Meals', icon: 'Users' }
  ];

  const menuItems = {
    popular: [
      {
        id: 1,
        name: "Lamb Shawarma Platter",
        description: "Tender lamb with rice, hummus & pita",
        price: 18.99,
        image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "15 min",
        rating: 4.9,
        orders: 247,
        dietary: ["Halal", "High Protein"]
      },
      {
        id: 2,
        name: "Chicken Kebab Feast",
        description: "Grilled chicken with saffron rice & tzatziki",
        price: 16.99,
        image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "12 min",
        rating: 4.8,
        orders: 189,
        dietary: ["Halal", "Gluten Free"]
      },
      {
        id: 3,
        name: "Mixed Grill Supreme",
        description: "Lamb, chicken & beef with all the sides",
        price: 28.99,
        image: "https://images.pexels.com/photos/5474641/pexels-photo-5474641.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "18 min",
        rating: 4.9,
        orders: 156,
        dietary: ["Halal", "Sharing"]
      }
    ],
    quick: [
      {
        id: 4,
        name: "Chicken Wrap",
        description: "Quick grilled chicken wrap with fresh veggies",
        price: 9.99,
        image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "5 min",
        rating: 4.7,
        orders: 312,
        dietary: ["Quick", "Portable"]
      },
      {
        id: 5,
        name: "Falafel Pita",
        description: "Crispy falafel with tahini in warm pita",
        price: 8.99,
        image: "https://images.pexels.com/photos/6107787/pexels-photo-6107787.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "6 min",
        rating: 4.6,
        orders: 278,
        dietary: ["Vegan", "Quick"]
      }
    ],
    healthy: [
      {
        id: 6,
        name: "Falafel Garden Bowl",
        description: "Fresh greens, quinoa, falafel & tahini",
        price: 14.99,
        image: "https://images.pexels.com/photos/6107787/pexels-photo-6107787.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "10 min",
        rating: 4.8,
        orders: 203,
        dietary: ["Vegan", "High Fiber"]
      },
      {
        id: 7,
        name: "Grilled Chicken Salad",
        description: "Mediterranean salad with grilled chicken",
        price: 13.99,
        image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "8 min",
        rating: 4.7,
        orders: 167,
        dietary: ["Gluten Free", "Low Carb"]
      }
    ],
    family: [
      {
        id: 8,
        name: "Family Feast Platter",
        description: "Serves 4-6 people with mixed meats & sides",
        price: 59.99,
        image: "https://images.pexels.com/photos/5474641/pexels-photo-5474641.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "25 min",
        rating: 4.9,
        orders: 89,
        dietary: ["Family Size", "Sharing"]
      },
      {
        id: 9,
        name: "Mezze Party Pack",
        description: "Assorted dips, pita & appetizers for sharing",
        price: 34.99,
        image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
        prepTime: "15 min",
        rating: 4.8,
        orders: 124,
        dietary: ["Vegetarian", "Sharing"]
      }
    ]
  };

  const recentOrders = [
    { id: 1, name: "Lamb Shawarma Platter", date: "2 days ago", rating: 5 },
    { id: 2, name: "Chicken Kebab Feast", date: "1 week ago", rating: 5 },
    { id: 3, name: "Falafel Garden Bowl", date: "2 weeks ago", rating: 4 }
  ];

  const updateQuantity = (itemId, change) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, (prev[itemId] || 0) + change)
    }));
  };

  const getItemQuantity = (itemId) => quantities[itemId] || 0;

  const getTotalItems = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={12}
        className={i < Math.floor(rating) ? 'text-accent fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Quick Order
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-2xl mx-auto">
            Skip the wait - order your favorites with just a few clicks
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Ordering Interface */}
          <div className="lg:col-span-3">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-cta font-semibold transition-organic ${
                    selectedCategory === category.id
                      ? 'bg-primary text-white shadow-warm'
                      : 'bg-background text-text-primary hover:bg-primary-50'
                  }`}
                >
                  <Icon name={category.icon} size={18} />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Menu Items Grid */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {menuItems[selectedCategory]?.map((item) => (
                <div key={item.id} className="bg-background rounded-lg shadow-warm overflow-hidden hover:shadow-warm-lg transition-organic">
                  {/* Item Image */}
                  <div className="relative h-48 overflow-hidden">
                    <Image
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3 bg-primary text-white px-2 py-1 rounded-full text-xs font-cta font-semibold">
                      {item.prepTime}
                    </div>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
                      {renderStars(item.rating)}
                      <span className="text-xs font-semibold ml-1">{item.rating}</span>
                    </div>
                  </div>

                  {/* Item Details */}
                  <div className="p-4">
                    <h3 className="font-cta font-bold text-lg text-text-primary mb-2">
                      {item.name}
                    </h3>
                    <p className="text-text-secondary text-sm mb-3 line-clamp-2">
                      {item.description}
                    </p>

                    {/* Dietary Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.dietary.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-trust-100 text-trust-700 rounded-full text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Price and Quantity Controls */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-cta font-bold text-xl text-primary">
                          ${item.price}
                        </span>
                        <div className="text-xs text-text-secondary">
                          {item.orders} orders this week
                        </div>
                      </div>

                      {getItemQuantity(item.id) === 0 ? (
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="bg-conversion text-white px-4 py-2 rounded-lg font-cta font-semibold hover:bg-conversion-600 transition-organic"
                        >
                          Add
                        </button>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-8 h-8 bg-primary-100 text-primary rounded-full flex items-center justify-center hover:bg-primary-200 transition-organic"
                          >
                            <Icon name="Minus" size={16} />
                          </button>
                          <span className="font-cta font-bold text-lg min-w-[2rem] text-center">
                            {getItemQuantity(item.id)}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-600 transition-organic"
                          >
                            <Icon name="Plus" size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Cart Summary */}
            {getTotalItems() > 0 && (
              <div className="bg-background rounded-lg p-6 shadow-warm">
                <h3 className="font-cta font-bold text-lg text-text-primary mb-4 flex items-center">
                  <Icon name="ShoppingBag" size={20} className="mr-2" />
                  Your Order ({getTotalItems()} items)
                </h3>
                
                <div className="space-y-3 mb-4">
                  {Object.entries(quantities).filter(([_, qty]) => qty > 0).map(([itemId, qty]) => {
                    const item = Object.values(menuItems).flat().find(i => i.id === parseInt(itemId));
                    if (!item) return null;
                    
                    return (
                      <div key={itemId} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-text-secondary"> x{qty}</span>
                        </div>
                        <span className="font-semibold">${(item.price * qty).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="border-t border-primary-100 pt-3 mb-4">
                  <div className="flex items-center justify-between font-cta font-bold">
                    <span>Total:</span>
                    <span className="text-primary">
                      ${Object.entries(quantities).reduce((total, [itemId, qty]) => {
                        const item = Object.values(menuItems).flat().find(i => i.id === parseInt(itemId));
                        return total + (item ? item.price * qty : 0);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <Link
                  to="/order-online-streamlined-experience"
                  className="w-full btn-conversion text-center block"
                >
                  Proceed to Checkout
                </Link>
              </div>
            )}

            {/* Recent Orders */}
            <div className="bg-background rounded-lg p-6 shadow-warm">
              <h3 className="font-cta font-bold text-lg text-text-primary mb-4 flex items-center">
                <Icon name="Clock" size={20} className="mr-2" />
                Reorder Favorites
              </h3>
              
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-surface rounded-lg">
                    <div>
                      <div className="font-medium text-text-primary text-sm">
                        {order.name}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {order.date}
                      </div>
                      <div className="flex items-center space-x-1 mt-1">
                        {renderStars(order.rating)}
                      </div>
                    </div>
                    <button className="text-primary hover:text-primary-600 transition-organic">
                      <Icon name="Plus" size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Info */}
            <div className="bg-gradient-to-br from-trust to-trust-600 rounded-lg p-6 text-white">
              <h3 className="font-cta font-bold text-lg mb-3 flex items-center">
                <Icon name="Truck" size={20} className="mr-2" />
                Delivery Info
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Delivery Time:</span>
                  <span className="font-semibold">15-25 min</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Delivery Fee:</span>
                  <span className="font-semibold">$2.99</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Free Delivery:</span>
                  <span className="font-semibold">Orders $25+</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link
            to="/interactive-menu-culinary-discovery"
            className="inline-flex items-center space-x-2 text-primary hover:text-primary-600 font-cta font-semibold text-lg transition-organic"
          >
            <span>View Full Menu</span>
            <Icon name="ArrowRight" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default OrderingWidget;